import { match } from 'ts-pattern'
import i18next from 'i18next'
import type { ProjectMutator } from '@/lib/entity/project'
import type { Converter } from '@/lib/converter/entityFormatter'
import { asModifiedField } from '@/lib/converter/entityFormatter'
import { DataBase } from '@/lib/kysely'
import { sql } from 'kysely'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'

type ProjectSchema = ProjectMutator['merged']

export const ProjectConverter: Converter<ProjectSchema> = {
  icon: () => i18next.t('stages.icon.project'),
  title: () => i18next.t('stages.table.project'),
  subTitle: async (merged) => merged.name ?? i18next.t('ui.common.inputting'),
  modifiedFields: (modified, column, context) => {
    const label = i18next.t(`stages.labels.project.${column}`)
    return match(column)
      .with('target_sales', () =>
        asModifiedField(
          label,
          (modified.target_sales != null ? modified.target_sales.toLocaleString() : '-') + ' 円'
        )
      )
      .with('target_cost', () =>
        asModifiedField(
          label,
          (modified.target_cost != null ? modified.target_cost.toLocaleString() : '-') + ' 円'
        )
      )
      .with('target_man_month', () =>
        asModifiedField(
          label,
          (modified.target_man_month != null ? modified.target_man_month.toLocaleString() : '-') +
            ' 人月'
        )
      )
      .with('client_id', async () =>
        asModifiedField(label, await getClientName(modified.client_id!))
      )
      .with('userIds', async () => {
        const filteredIds = (modified.userIds || []).filter((id): id is string => id !== undefined)
        return asModifiedField(label, await getConcatenatedNames('user', filteredIds))
      })
      .with('custom_fields', async () => {
        const baseCustomFields = (await getCustomField(context?.id)) ?? {}
        const modifiedCustomFields = JSON.parse(modified.custom_fields ?? '{}')
        const differences = getDifferences(baseCustomFields, modifiedCustomFields)
        const keys = [
          ...new Set([
            ...Object.keys(baseCustomFields ?? {}),
            ...Object.keys(modifiedCustomFields ?? {})
          ])
        ]

        // 一度のクエリで対応するcustom_fieldをすべて取得
        const customFields = await DataBase.selectFrom('custom_field')
          .select(['key', 'name'])
          .where('key', 'in', keys)
          .execute()
        const customFieldNameMap = customFields.reduce(
          (acc, field) => {
            acc[field.key] = field.name
            return acc
          },
          {} as Record<string, string>
        )

        const changeSummary: string[] = []
        differences.deleteKeys.forEach((key) => {
          const fieldName = customFieldNameMap[key]
          if (fieldName) {
            changeSummary.push('\n' + fieldName + ': ' + '-')
          }
        })
        differences.changedKeys.forEach((key) => {
          const value = modifiedCustomFields[key]
          const fieldName = customFieldNameMap[key]
          let displayValue = value
          if (Array.isArray(value) && value.length === 0) {
            displayValue = '-'
          }
          if (fieldName) {
            changeSummary.push('\n' + fieldName + ': ' + displayValue)
          }
        })

        return asModifiedField(label, changeSummary.join(''))
      })
      .with('departmentIds', async () => {
        const filteredIds = (modified.departmentIds || []).filter(
          (id): id is string => id !== undefined
        )
        return asModifiedField(label, await getConcatenatedNames('department', filteredIds))
      })
      .with('detail', async () => {
        return asModifiedField(label, truncateString(modified.detail!))
      })
      .with('target_sales_breakdown_plan', async () => {
        const targetSalesBreakdown: { offset: number; sales: number }[] = JSON.parse(
          modified.target_sales_breakdown_plan ?? '[]'
        )

        if (targetSalesBreakdown.length === 0) {
          return asModifiedField(label, '自動按分')
        }

        return asModifiedField(
          label,
          '(手動入力)' +
            targetSalesBreakdown.map((breakdown) => breakdown.sales.toLocaleString()).join(',')
        )
      })
      .with('id', 'name', 'start_date', 'end_date', 'code', async (column) =>
        asModifiedField(label, modified[column]!)
      )
      .with('target_man_hours_in_sec', () => undefined)
      .exhaustive()
  }
}

const getClientName = async (clientId: string) => {
  const clientName = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom('client')
        .select(['name'])
        .where('id', '=', clientId)
        .executeTakeFirst()) || { name: '' }
    )
  })
  return clientName.name
}

const truncateString = (str: string | null, length = 15): string => {
  if (str === null) return ''
  return str.length > length ? str.substring(0, length) + '...' : str
}

const getConcatenatedNames = async (
  tableName: 'department' | 'job_title' | 'job_skill' | 'user' | 'assign',
  ids: string[]
): Promise<string> => {
  const result = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom(tableName)
        .select([sql<string>`IFNULL(group_concat(name),'')`.as('names')])
        .where('id', 'in', ids)
        .executeTakeFirst()) || { names: '' }
    )
  })
  return truncateString(result.names)
}

const getCustomField = async (projectId: string) => {
  const result = await DataBase.selectFrom('project')
    .select(['custom_fields'])
    .where('id', '=', projectId)
    .executeTakeFirst()

  return result ? result.custom_fields : null
}

const getDifferences = (
  baseObj: Record<string, any> | null,
  modifiedObj: Record<string, any> | null
): { deleteKeys: string[]; changedKeys: string[] } => {
  const deleteKeys: string[] = []
  const changedKeys: string[] = []

  // null チェック
  if (baseObj === null || modifiedObj === null) {
    return { deleteKeys, changedKeys }
  }

  Object.keys(baseObj).forEach((key) => {
    if (key in modifiedObj) {
      if (!objEqual(baseObj[key], modifiedObj[key])) {
        changedKeys.push(key)
      }
    } else {
      deleteKeys.push(key)
    }
  })

  Object.keys(modifiedObj).forEach((key) => {
    if (!(key in baseObj)) {
      changedKeys.push(key)
    }
  })

  return { deleteKeys, changedKeys }
}

const objEqual = (obj1: Record<string, any> | null, obj2: Record<string, any> | null): boolean => {
  if (obj1 === obj2) {
    return true
  }
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
    return false
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !objEqual(obj1[key], obj2[key])) {
      return false
    }
  }
  return true
}
